<template>
  <div
      class="select-field-wrapper fs-regular"
      :class="{
        'open': isOpen,
        'hide-selected': hideSelectedOption,
        'fix-selected': fixSelectedOption,
      }"
      ref="root"
  >
    <div
        class="input-field input-field--select"
        :class="{
          'valid': !showLoader && !hasError && showValidCheck,
          'loading': showLoader,
          'icon-right': showLoader,
          'filled': filled,
          'focused': isOpen,
          'disabled': disabled,
          'readonly': readonly,
          'label-hidden': hideLabel,
        }"
        :style="cssVariables"
        @click="openSelect"
    >
      <div
          v-if="iconLeft"
          class="col-icon-left"
      >
        <img :src="iconLeft" :alt="`Icon ${selectedLabel}`">
      </div>
      <div class="col-main">
        <div>
          <label v-if="!hideLabel" v-html="label"></label>
          <div
              class="input-label"
              v-html="selectedLabel || '&nbsp;'"
          ></div>
          <input
              type="text"
              readonly
              :value="selectedLabel"
              @focusin="openSelect"
          >
        </div>
      </div>
      <div
          v-if="showLoader"
          class="col-icon-right"
      >
        <span class="input-icon--right">
          <FontAwesomeIcon
              class="fa-pulse"
              :icon="faSpinner"
          />
        </span>
      </div>
      <div
          v-if="options.length > 1"
          class="col-chevron"
      >
        <span class="input-icon--dropdown">
            <FontAwesomeIcon :icon="faChevronDown"/>
        </span>
      </div>
      <div
          v-if="showValidCheck || showInvalidCross"
          class="col-check"
      >
        <div
            v-if="showValidCheck"
            class="valid-check"
            :style="{
              background: validCheckColor,
            }"
        >
          <div class="icon">
            <FontAwesomeIcon :icon="faCheck"/>
          </div>
        </div>
        <Tooltip
            v-else
            class="invalid-check"
            is-error
        >
          <div class="icon">
            <FontAwesomeIcon :icon="faExclamation"/>
          </div>
          <template #title>
            <span v-html="errorMessage"></span>
          </template>
        </Tooltip>
      </div>
    </div>
    <Teleport to="body">
      <div
          v-if="isOpen"
          ref="selectFieldList"
          class="select-field-list font-regular"
          :class="{ 'search': search, [customListClass || 'undef']: !!customListClass }"
          :data-transform="headerInitialyOpen"
      >
        <div v-if="search">
          <div>
            <input
                type="text"
                ref="searchInput"
                v-model="searchFor"
            />
          </div>
          <div>
            <FontAwesomeIcon :icon="faSearch"/>
          </div>
        </div>
        <ul>
          <li
              v-for="(option, index) in searchedOptions"
              :key="getOptionKey(option)"
              :class="{'selected': getOptionValue(option) === modelValue }"
              @click="setValue(option)"
          >
            <slot
                name="option"
                v-bind="{
                  option,
                  index,
                  value: getOptionValue(option),
                  key: getOptionKey(option),
                  selected: getOptionValue(option) === modelValue
                }"
            >
              <template v-html="getOptionLabel(option)" v-inline-html/>
            </slot>
          </li>
        </ul>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import Tooltip from '@/components/Tooltip.vue';
import {
  computed,
  ref,
  watch,
  nextTick,
} from 'vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faSpinner,
  faCheck,
  faExclamation,
  faSearch,
} from '@fortawesome/pro-solid-svg-icons';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';

library.add(faCheck, faChevronDown, faSearch, faSpinner, faExclamation);

const modelValue = defineModel();
const error = defineModel<undefined | string | string[]>('error');
const props = withDefaults(defineProps<{
  search?: boolean
  name?: string
  label?: string
  options?: Array<string | { label: string, value: any, icon?: string }>
  isValid?: boolean
  validColor?: string | Array<string | undefined>
  readonly?: boolean
  disabled?: boolean
  hideLabel?: boolean
  hideSelectedOption?: boolean
  fixSelectedOption?: boolean
  showLoader?: boolean
  clearable?: boolean
  customListClass?: string
  iconLeft?: string
}>(), {
  label: '&nbsp;',
  options: () => [],
});

const root = ref();
const selectFieldList = ref();
const searchInput = ref();

const componentError = ref(error.value);
const isOpen = ref(false);
const searchFor = ref<string>('');
watch(() => error.value, () => {
  componentError.value = error.value;
}, {
  immediate: true,
  deep: true,
});
watch(() => modelValue.value, () => {
  error.value = undefined;
}, {
  deep: true,
});

const errorMessage = computed<string | undefined>(() => {
  if (Array.isArray(componentError.value)) {
    if (componentError.value[0]) {
      return `${componentError.value[0]}`.trim();
    }
  } else if (componentError.value) {
    return `${componentError.value}`.trim();
  }

  return undefined;
});
const hasError = computed<boolean>(() => errorMessage.value !== undefined);

const showValidCheck = computed(() => props.isValid);
const validCheckColor = computed(() => {
  let colors = Array.isArray(props.validColor) ? props.validColor : [props.validColor];
  colors = colors.filter((color) => !!color).map((color) => `${color}`.trim());

  if (colors.length >= 2) {
    return `linear-gradient(90deg, ${colors[0]} 0%, ${colors[1]} 100%)`;
  }

  return colors[0];
});

const showInvalidCross = computed(() => errorMessage.value !== undefined && !showValidCheck.value);

const getOptionValue = ($option: string | number | Record<string, unknown>)
    : string | number => {
  if (typeof $option === 'object') {
    return $option.value as string | number;
  }

  return $option;
};

const getOptionLabel = ($option: string | number | Record<string, unknown>) => {
  if (typeof $option === 'object') {
    return $option.label;
  }

  return $option;
};

const getOptionKey = ($option: string | number | Record<string, unknown>) => {
  let $label = $option as string;

  if (typeof $option === 'object') {
    $label = $option.label as string;
  }

  return `${$label}`.replace(/[\W_]+/g, ' ');
};

const selectedLabel = computed(() => {
  let $label = null;

  const $selectedOption = [...props.options].filter(($option) => `${getOptionValue($option as string | Record<string, string>)}` === `${modelValue.value}`);
  if ($selectedOption.length) {
    $label = getOptionLabel($selectedOption[0] as string | Record<string, string>);
  }

  return $label;
});

const scrollEventKeys = [37, 38, 39, 40];
const scrollEventPreventDefault = (e: Event) => {
  if (!!e.composedPath && e.composedPath().includes(selectFieldList.value)) {
    const isElementScrollable = (selectFieldList.value?.querySelector('ul')?.scrollHeight || 0) > (selectFieldList.value?.querySelector('ul')?.clientHeight || 0);

    if (isElementScrollable) return;
  }

  e.preventDefault();
};
const scrollEventPreventDefaultScrollKeys = (e: KeyboardEvent) => {
  if (!!e.composedPath && e.composedPath().includes(selectFieldList.value)) {
    return;
  }

  if (scrollEventKeys.includes(e.keyCode)) {
    e.preventDefault();
  }
};

let scrollEventSupportsPassive = false;
try {
  window.addEventListener('checkSupportsPassive', null as any, Object.defineProperty({}, 'passive', {
    get: () => {
      scrollEventSupportsPassive = true;
      return false;
    },
  }) as AddEventListenerOptions);
} catch (e) {
  // do nothing if this doesn't work
}

const wheelOpt = scrollEventSupportsPassive ? { passive: false } : false;
const wheelEvent = 'onwheel' in document.createElement('div') ? 'wheel' : 'mousewheel';

const disableScrolling = (): void => {
  window.addEventListener('DOMMouseScroll', scrollEventPreventDefault, false);
  window.addEventListener(wheelEvent, scrollEventPreventDefault, wheelOpt as AddEventListenerOptions);
  window.addEventListener('touchmove', scrollEventPreventDefault, wheelOpt as AddEventListenerOptions);
  window.addEventListener('keydown', scrollEventPreventDefaultScrollKeys, false);
};

function enableScroll() {
  window.removeEventListener('DOMMouseScroll', scrollEventPreventDefault, false);
  window.removeEventListener(wheelEvent, scrollEventPreventDefault, wheelOpt as AddEventListenerOptions);
  window.removeEventListener('touchmove', scrollEventPreventDefault, wheelOpt as AddEventListenerOptions);
  window.removeEventListener('keydown', scrollEventPreventDefaultScrollKeys, false);
}

const isSafari = navigator.vendor.match(/apple/i)
    && !navigator.userAgent.match(/crios/i)
    && !navigator.userAgent.match(/fxios/i)
    && !navigator.userAgent.match(/Opera|OPT\//);

const headerInitialyOpen = ref<string | undefined>('true');
const setListStylesInterval = ref();
const setListStyles = () => {
  if (root.value) {
    const headerOpen = !document.querySelector('header.slide-up') ? 'true' : 'false';
    if (headerOpen !== headerInitialyOpen.value && isSafari) {
      headerInitialyOpen.value = undefined;
    }
    const rootPosition = root.value.getBoundingClientRect();
    const body = document.querySelector('body');

    const bottomOffset = rootPosition.bottom;
    let leftOffset = rootPosition.left;
    let widthOffset = rootPosition.width;

    const calculatorNode: Element | undefined = [...document.querySelectorAll('.calculator')].find((calcNode: Node) => calcNode.contains(root.value));
    const calculatorMainRow = calculatorNode?.querySelector('.row.row-main');

    if (calculatorMainRow) {
      let parentCol = root.value.parentNode;

      while (parentCol.parentNode && parentCol.parentNode !== calculatorMainRow) {
        parentCol = parentCol.parentNode;
      }

      if (parentCol) {
        const parentColStyles = window.getComputedStyle(parentCol, null);
        const parentColPaddingLeft = parseInt(parentColStyles.getPropertyValue('padding-left'), 10);
        const parentColPaddingRight = parseInt(parentColStyles.getPropertyValue('padding-right'), 10);

        leftOffset -= parentColPaddingLeft + 1;
        widthOffset += parentColPaddingLeft + parentColPaddingRight + 2;
      }
    }

    body?.style.setProperty('--select-options-list-position', 'fixed');
    body?.style.setProperty('--select-options-list-top', `${bottomOffset}px`);
    body?.style.setProperty('--select-options-list-left', `${leftOffset}px`);
    body?.style.setProperty('--select-options-list-width', `${widthOffset}px`);
  }
};

const removeListStyles = () => {
  const body = document.querySelector('body');

  // eslint-disable-next-line no-unused-expressions
  body?.style.removeProperty('--select-options-list-top');
  // eslint-disable-next-line no-unused-expressions
  body?.style.removeProperty('--select-options-list-left');
  // eslint-disable-next-line no-unused-expressions
  body?.style.removeProperty('--select-options-list-width');

  if (setListStylesInterval.value) {
    clearInterval(setListStylesInterval.value);
    setListStylesInterval.value = undefined;
  }

  enableScroll();
};

const closeMenuList = () => {
  setTimeout(() => {
    isOpen.value = false;
    removeListStyles();
    searchFor.value = '';
    window.removeEventListener('scroll', setListStyles, {
      passive: true,
    } as EventListenerOptions);
    if (searchInput.value) {
      searchInput.value.removeEventListener('focusout', closeMenuList);
    }
  }, 150);
};

const ifClickedOutside = (event: MouseEvent) => {
  if (root.value) {
    const withinBoundaries = event.composedPath().includes(root.value);

    if (!withinBoundaries) {
      closeMenuList();
    }
  }
};

const openSelect = () => {
  if (props.options.length > 1) {
    headerInitialyOpen.value = !document.querySelector('header.slide-up') ? 'true' : 'false';
    setListStyles();
    if (isSafari) {
      setListStylesInterval.value = setInterval(setListStyles, 75);
      window.addEventListener('scroll', setListStyles, {
        passive: true,
      } as EventListenerOptions);
    }
    disableScrolling();
    nextTick(() => {
      isOpen.value = true;

      nextTick(() => {
        document.addEventListener('click', ifClickedOutside);
        if (searchInput.value) {
          searchInput.value.focus({
            preventScroll: true,
          });

          searchInput.value.addEventListener('focusout', closeMenuList);
        }
      });
    });
  }
};

const setValue = ($option: string | Record<string, string>) => {
  if (props.clearable && modelValue.value === getOptionValue($option)) {
    modelValue.value = undefined;
  } else {
    modelValue.value = getOptionValue($option);
  }
  isOpen.value = false;
  removeListStyles();
  searchFor.value = '';
};

watch(() => isOpen.value, ($isOpen) => {
  if (!$isOpen) {
    document.removeEventListener('click', ifClickedOutside);
  }
});

const searchedOptions = computed(() => {
  if (`${searchFor.value || ''}`.trim() === '') return [...props.options];

  return [...props.options].filter(($option) => {
    const $optionVal = getOptionValue($option as string | Record<string, string>);
    const $optionLabel = getOptionLabel($option as string | Record<string, string>);

    const valueIncludesSearch = `${$optionVal}`
      .toLowerCase()
      .includes((searchFor.value || '').toLowerCase());
    const labelIncludesSearch = `${$optionLabel}`
      .toLowerCase()
      .includes((searchFor.value || '').toLowerCase());

    return valueIncludesSearch || labelIncludesSearch;
  });
});

const possibleValues = computed(() => Object.values(props.options).map((option: any) => (typeof option === 'object' ? option.value : option)));
const filled = computed(() => possibleValues.value.includes(modelValue.value));

interface GridItems {
  iconLeft?: string
  main: string
  loader?: string
  chevron: string
  check?: string
}
const inputGrid = computed<{
  items: GridItems
  columns: string[]
}>(() => {
  const columns = [];
  const items: GridItems = {
    main: '1 / 2',
    chevron: '2 / 3',
  };

  if (props.iconLeft) {
    columns.push('auto');
    items.iconLeft = `${columns.length} / ${columns.length + 1}`;
  }

  columns.push('1fr');
  items.main = `${columns.length} / ${columns.length + 1}`;

  if (props.showLoader) {
    columns.push('auto');
    items.loader = `${columns.length} / ${columns.length + 1}`;
  }

  columns.push('auto');
  items.chevron = `${columns.length} / ${columns.length + 1}`;

  if (showValidCheck.value || showInvalidCross.value) {
    columns.push('auto');
    items.check = `${columns.length} / ${columns.length + 1}`;
  }

  return {
    items,
    columns,
  };
});

const cssVariables = computed<Record<string, string | undefined>>(() => ({
  '--grid-columns': inputGrid.value.columns.join(' '),
  '--grid-column-icon-left': inputGrid.value.items.iconLeft,
  '--grid-column-main': inputGrid.value.items.main,
  '--grid-column-loader': inputGrid.value.items.loader,
  '--grid-column-chevron': inputGrid.value.items.chevron,
  '--grid-column-chevron-padding': inputGrid.value.items.check ? '12px' : '1.875em',
  '--grid-column-check': inputGrid.value.items.check,
}));
</script>

<style lang="scss">
@import 'src/scss/component-helpers';

.select-field-wrapper {
  position: relative;

  .borderless + .select-field-list {
    border-bottom-left-radius: 7px;
    border-bottom-right-radius: 7px;
    border: 0;
    box-shadow: 0 6px 6px rgba(0, 0, 0, .16);
  }

  &.hide-selected {
    ul li.selected {
      display: none;
    }
  }

  &.fix-selected {
    ul li.selected {
      order: 0;
    }
  }
}

.select-field-list {
  z-index: 9999;
  transition: transform .5s linear;
  font-weight: 400;
  background-color: #FFFFFF;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  padding: 0;
  border-top: 0;
  border-bottom-left-radius: 1.625em;
  border-bottom-right-radius: 1.625em;
  overflow: hidden;
  font-size: 1rem;
  position: var(--select-options-list-position);
  top: var(--select-options-list-top);
  left: var(--select-options-list-left);
  width: var(--select-options-list-width);
  @include font-regular(16px);

  &[data-transform="true"] {
    transform: translateY(calc(#{var(--app-header-height-transformed)} - #{var(--app-header-height)}));
  }

  &[data-transform="false"] {
    transform: translateY(#{var(--app-header-height-transformed)});
  }

  > ul {
    display: flex;
    list-style: none;
    padding: 0;
    font-size: inherit;
    max-height: 20em;
    overflow: auto;
    margin: 0;
    width: 100%;
    flex-wrap: wrap;

    li {
      display: block;
      font-size: inherit;
      padding: .5em 1.875em;
      color: #38383A;
      cursor: default;
      width: 100%;
      max-width: 100%;
      flex: 1 0 100%;
      order: 1;

      &:hover, &.selected {
        background-color: #ECF2F6;
      }

      &:not(.selected) {
        cursor: pointer;
      }
    }
  }

  &.search {
    > ul {
      max-height: 17.35em;
    }

    > div:first-child {
      border-bottom: 1px solid #ECF2F6;
      border-top: 1px solid #ECF2F6;
      cursor: default;
      background-color: transparent !important;
      display: grid;
      grid-template-rows: 100%;
      grid-template-columns: 1fr auto;
      padding: .5em 1.875em;
      align-items: center;

      > div {
        &:first-child {
          grid-column: 1 / 2;
        }

        &:last-child {
          grid-column: 2 / 3;
          padding-left: 12px;
        }
      }

      input {
        border: 0;
        outline: none;
        width: 100%;
        background-color: transparent !important;
        font-size: inherit;
        color: #38383A;
        padding: 0;
      }

      i, svg {
        color: #ECF2F6;
        pointer-events: none;
      }
    }
  }
}
</style>
